import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import {
  StyleSheet,
  Text,
  View,
  FlatList,
  Pressable,
  Platform,
  useWindowDimensions,
} from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import { s, fs, iconSize, v } from '../../utils/libs';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';
import { ActivityIndicator } from 'react-native-paper';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { listEmployees } from '../../api/employees';
import {
  setEmployees,
  setEmployeesError,
  setEmployeesLoading,
  setEmployeesPagination,
  setSelectedEmployeeId,
} from '../../redux/backoffice/employeesSlice';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/AppStack';
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../../components/Reusables/AppSearchBar.web';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
// import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import { isTablet } from 'react-native-device-info';
import { useOwnerActivationBootstrap } from '../../hooks/useOwnerActivationBootstrap';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useAuth from '../../hooks/useAuth';
import {
  clearAssignableStaffForLocation,
  fetchAssignableStaffForLocation,
  selectAssignableStaffForLocation,
} from '../../redux/assignableStaffSlice';
import { selectValidCurrentLocation } from '../../redux/locationSlice';

type Props = {
  embedded?: boolean;
};

const BackOfficeEmployeesScreen: React.FC<Props> = ({ embedded = false }) => {
  const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const isFocused = useIsFocused();
  const { useSidebarNav } = useLayoutSize();
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { width, height } = useWindowDimensions();
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const { owner } = useBackofficeOwnerAuth();
  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const employees = useAppSelector(s => s.backofficeEmployees.items);
  const loading = useAppSelector(s => s.backofficeEmployees.loading);
  const page = useAppSelector(s => s.backofficeEmployees.page);
  const pageSize = useAppSelector(s => s.backofficeEmployees.pageSize);
  const total = useAppSelector(s => s.backofficeEmployees.total);
  const locationVersion = useAppSelector(s => s.location.locationVersion);
  const currentLocation = useAppSelector(selectValidCurrentLocation);
  const currentLocationId = currentLocation?._id ?? null;
  const isTestLocation =
    currentLocation?.bp_location_is_system_generated === true;
  const assignableStaffEntry = useAppSelector(state =>
    selectAssignableStaffForLocation(state, currentLocationId || ''),
  );
  const { reportBootstrapEvidence } = useOwnerActivationBootstrap();
  const [staffSearchTerm, setStaffSearchTerm] = useState('');

  const headerRightNode = useMemo(
    () => (
      <Pressable
        onPress={() => nav.navigate('BackOfficeAddEmployee')}
        style={({ pressed, hovered }: any) => [
          styles.headerCTA,
          pressed && styles.headerCTAPressed,
          Platform.OS === 'web' && hovered && styles.headerCTAHovered,
        ]}>
        <Text style={styles.headerCTAText}>+ Add New</Text>
      </Pressable>
    ),
    [nav, styles],
  );

  const searchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: nativeHeaderSearchWidth,
        }}>
        <AppSearchBar
          placeholder="Search staff..."
          value={staffSearchTerm}
          onChangeText={setStaffSearchTerm}
          onClear={() => setStaffSearchTerm('')}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={nativeHeaderSearchWidth}
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [nativeHeaderSearchWidth, staffSearchTerm, theme.colors.surface],
  );

  const webSearchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: width >= 768 && width < 1024 ? s(660) : s(620),
        }}>
        <AppSearchBarWeb
          placeholder="Search staff name or role"
          value={staffSearchTerm}
          onChangeText={setStaffSearchTerm}
          onClear={() => setStaffSearchTerm('')}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={
            showTabletSidebarLayoutSetting && useSidebarNav
              ? width * 0.46
              : width * 0.32
          }
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [
      showTabletSidebarLayoutSetting,
      staffSearchTerm,
      theme.colors.surface,
      useSidebarNav,
      width,
    ],
  );

  useLayoutEffect(() => {
    if (useSidebarNav) return;
    if (embedded && !isFocused) return;
    const targetNav = embedded ? (nav.getParent() ?? nav) : nav;
    (targetNav as any).setOptions({
      headerTitleContainerStyle: { flex: 1 },
      headerTitle: () => searchBar,
      headerRight: () => headerRightNode,
    });
  }, [embedded, headerRightNode, isFocused, nav, searchBar, useSidebarNav]);

  const webHeaderNode = useMemo(() => {
    if (!useSidebarNav) return null;
    return (
      <WebHeaderBar
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            (nav as any).navigate('BackOffice', { screen: 'Dashboard' });
          } catch {
            if (nav.canGoBack()) nav.goBack();
          }
        }}>
        {webSearchBar}
      </WebHeaderBar>
    );
  }, [headerRightNode, nav, useSidebarNav, webSearchBar]);

  useWebHeader(
    webHeaderNode,
    Boolean(webHeaderNode) && (!embedded || isFocused),
  );

  const ownerId = String(employee?.bp_owner_id || '');

  const fetchEmployees = useCallback(
    async (targetPage: number = page) => {
      if (!owner?.accessToken || !ownerId) return;
      try {
        dispatch(setEmployeesLoading(true));
        dispatch(setEmployeesError(null));
        const res = await listEmployees(
          axiosBackoffice as any,
          ownerId,
          targetPage,
          pageSize,
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200) {
          const incoming: any[] = res?.data?.data?.data || [];
          const totalCount = res?.data?.data?.total || incoming.length;
          const combined =
            targetPage > 1 ? [...employees, ...incoming] : incoming;
          const dedupedMap = new Map<string, any>();
          for (const emp of combined) {
            const key = String(emp?._id || '');
            if (!key) continue;
            dedupedMap.set(key, { ...(dedupedMap.get(key) || {}), ...emp });
          }
          const nextItems = Array.from(dedupedMap.values());
          dispatch(
            setEmployees({
              items: nextItems,
              total: totalCount,
              page: targetPage,
              pageSize,
            }),
          );
        } else {
          dispatch(
            setEmployeesError(
              String(res?.data?.message || 'Failed to load employees'),
            ),
          );
        }
      } catch (e: any) {
        dispatch(setEmployeesError(e?.message || 'Error fetching employees'));
      } finally {
        dispatch(setEmployeesLoading(false));
      }
    },
    [
      owner?.accessToken,
      ownerId,
      dispatch,
      axiosBackoffice,
      page,
      pageSize,
      employees,
    ],
  );

  useEffect(() => {
    fetchEmployees(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationVersion, page]);

  // Clear prior assignable-staff entry on location context change so a failed
  // fetch can retry (errors must not stick forever for activation bootstrap).
  useEffect(() => {
    if (!currentLocationId || isTestLocation) return;
    dispatch(clearAssignableStaffForLocation(currentLocationId));
  }, [currentLocationId, dispatch, isTestLocation, locationVersion]);

  // Location-scoped add_staff bootstrap: assignable staff only (never listEmployees).
  useEffect(() => {
    if (!currentLocationId || isTestLocation) return;
    if (!auth?.accessToken) return;
    if (
      assignableStaffEntry.loading ||
      assignableStaffEntry.featureDisabled ||
      !!assignableStaffEntry.fetchedAt ||
      !!assignableStaffEntry.error
    ) {
      return;
    }
    void dispatch(
      fetchAssignableStaffForLocation({
        axiosinstance: axiosPrivate as any,
        auth,
        locationId: currentLocationId,
      }) as any,
    );
  }, [
    assignableStaffEntry.error,
    assignableStaffEntry.featureDisabled,
    assignableStaffEntry.fetchedAt,
    assignableStaffEntry.loading,
    auth,
    axiosPrivate,
    currentLocationId,
    dispatch,
    isTestLocation,
    locationVersion,
  ]);

  useEffect(() => {
    if (!currentLocationId || isTestLocation) return;
    if (assignableStaffEntry.loading || assignableStaffEntry.error) return;
    if (assignableStaffEntry.fetchedAt == null) return;
    const ownerEmployeeId = String(employee?._id || '');
    const hasNonOwner = (assignableStaffEntry.staff || []).some(row => {
      const id = String(row?.id || '');
      return !!id && id !== ownerEmployeeId;
    });
    if (!hasNonOwner) return;
    reportBootstrapEvidence([
      {
        taskId: 'add_staff',
        verifiedAt: new Date().toISOString(),
        hasNonOwnerEmployee: true,
        locationId: currentLocationId,
      },
    ]);
  }, [
    assignableStaffEntry.error,
    assignableStaffEntry.fetchedAt,
    assignableStaffEntry.loading,
    assignableStaffEntry.staff,
    currentLocationId,
    employee?._id,
    isTestLocation,
    reportBootstrapEvidence,
  ]);

  const hasMore = employees.length < total;

  const filteredEmployees = useMemo(() => {
    const query = String(staffSearchTerm || '')
      .trim()
      .toLowerCase();
    if (!query) return employees;

    return employees.filter((staff: any) => {
      const name = String(staff?.bp_employee_name || '').toLowerCase();
      const role = String(staff?.bp_employee_role || '').toLowerCase();
      const email = String(
        staff?.bp_employee_email_address || '',
      ).toLowerCase();
      const phone = String(staff?.bp_employee_phone_number || '').toLowerCase();

      return (
        name.includes(query) ||
        role.includes(query) ||
        email.includes(query) ||
        phone.includes(query)
      );
    });
  }, [employees, staffSearchTerm]);

  const handleEmployeePress = useCallback(
    (emp: any) => {
      dispatch(setSelectedEmployeeId(emp._id));
      // @ts-ignore
      nav.navigate('BackOfficeEmployeeDetails', { employeeId: emp._id });
    },
    [dispatch, nav],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: any; index: number }) => {
      const active = !item?.bp_employee_account_information_is_locked;
      const name: string = item?.bp_employee_name || 'N/A';
      const role: string = item?.bp_employee_role || '-';
      const phone = String(item?.bp_employee_phone_number || '').trim();
      const hasPhone = phone.length > 0;

      return (
        <Pressable
          unstable_pressDelay={100}
          onPress={() => handleEmployeePress(item)}
          style={({ pressed, hovered }: any) => [
            styles.rowCard,
            pressed && styles.rowCardPressed,
            Platform.OS === 'web' && hovered && styles.rowCardHovered,
          ]}>
          <View style={styles.rowNumberColumn}>
            <Text
              style={[
                styles.rowNumberText,
                !active && styles.rowNumberTextInactive,
              ]}>
              {index + 1}
            </Text>
          </View>
          <View style={styles.rowContent}>
            <View style={styles.rowTop}>
              <View style={styles.rowTitleWrap}>
                <View
                  style={[
                    styles.statusDot,
                    active ? styles.statusDotActive : styles.statusDotInactive,
                  ]}
                />
                <Text
                  style={[styles.rowTitle, !active && styles.rowTitleInactive]}
                  numberOfLines={1}>
                  {name}
                </Text>
                {!active && (
                  <Text style={styles.inactiveStatusLabel}>Inactive</Text>
                )}
              </View>
              <Text
                style={[
                  styles.rowRoleText,
                  !active && styles.rowSubtextInactive,
                ]}
                numberOfLines={1}>
                {role}
              </Text>
            </View>
            <Text
              style={[
                styles.rowSubtext,
                hasPhone && styles.phoneLinkText,
                !active && styles.rowSubtextInactive,
              ]}
              numberOfLines={1}>
              {hasPhone ? phone : '—'}
            </Text>
          </View>
        </Pressable>
      );
    },
    [handleEmployeePress, styles],
  );

  const keyExtractor = useCallback(
    (item: any, index: number) => String(item?._id || index),
    [],
  );

  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    if (page === 1) {
      fetchEmployees(1);
      return;
    }
    dispatch(setEmployeesPagination({ page: 1 }));
  }, [dispatch, fetchEmployees, page]);

  useEffect(() => {
    if (!loading && refreshing) {
      setRefreshing(false);
    }
  }, [loading, refreshing]);

  const onEndReached = useCallback(() => {
    if (loading) return;
    if (hasMore) {
      dispatch(setEmployeesPagination({ page: page + 1 }));
    }
  }, [dispatch, page, hasMore, loading]);

  return (
    <View style={[styles.container, embedded && styles.containerEmbedded]}>
      <View style={styles.listContainer}>
        <FlatList
          data={filteredEmployees}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          onEndReachedThreshold={0.6}
          onEndReached={onEndReached}
          refreshing={refreshing}
          onRefresh={onRefresh}
          ListHeaderComponent={
            <View style={styles.listHeader}>
              <Text style={styles.listHeaderNumberText}>#</Text>
              <Text style={[styles.listHeaderText, styles.listHeaderNameText]}>
                Name
              </Text>
              <Text style={styles.listHeaderText}>Role</Text>
            </View>
          }
          ListEmptyComponent={
            loading ? (
              <View style={styles.loadingWrap}>
                <ActivityIndicator />
                <Text style={styles.loadingText}>Loading staff...</Text>
              </View>
            ) : (
              <View style={styles.emptyWrap}>
                <MaterialCommunityIcons
                  name="account-plus-outline"
                  size={iconSize(64)}
                  color={theme.colors.textTertiary}
                />
                <Text style={styles.emptyTitle}>No staff found</Text>
                <Text style={styles.emptySubtitle}>
                  Add your first team member.
                </Text>
                <Pressable
                  onPress={() => nav.navigate('BackOfficeAddEmployee')}
                  style={({ pressed, hovered }: any) => [
                    styles.emptyCTA,
                    pressed && styles.emptyCTAPressed,
                    Platform.OS === 'web' && hovered && styles.emptyCTAHovered,
                  ]}>
                  <Text style={styles.emptyCTAText}>+ Add New</Text>
                </Pressable>
              </View>
            )
          }
          ListFooterComponent={
            loading && employees.length > 0 ? (
              <View style={styles.footerLoading}>
                <ActivityIndicator />
              </View>
            ) : null
          }
        />
      </View>
    </View>
  );
};

export default BackOfficeEmployeesScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      padding: s(5),
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    containerEmbedded: {
      paddingTop: 0,
    },
    headerCTA: {
      marginRight: s(8),
      paddingHorizontal: s(10),
      paddingVertical: v(6),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    headerCTAHovered: {
      backgroundColor: 'rgba(255,255,255,0.14)',
    } as any,
    headerCTAPressed: {
      opacity: 0.75,
    },
    headerCTAText: {
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(16),
    },
    loadingWrap: {
      alignItems: 'center',
      paddingVertical: v(36),
    },
    loadingText: {
      marginTop: v(10),
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    emptyWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(20),
      paddingVertical: v(40),
      gap: v(10),
    },
    emptyTitle: {
      textAlign: 'center',
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    emptySubtitle: {
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      fontSize: fs(13),
      lineHeight: fs(18),
    },
    listContainer: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      ...theme.shadows.md,
    },
    listHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: s(12),
      paddingVertical: v(10),
      backgroundColor: theme.colors.surfaceSecondary,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    listHeaderText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    listHeaderNameText: {
      flex: 1,
    },
    listHeaderNumberText: {
      width: s(16),
      marginRight: s(10),
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
      textAlign: 'center',
    },
    rowCard: {
      flexDirection: 'row',
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      paddingHorizontal: s(12),
      paddingVertical: v(10),
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    rowCardHovered: {
      backgroundColor: theme.colors.surfaceSecondary,
    } as any,
    rowCardPressed: {
      opacity: 0.75,
    },
    rowNumberColumn: {
      width: s(16),
      marginRight: s(10),
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowNumberText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    rowContent: {
      flex: 1,
      minWidth: 0,
    },
    rowTop: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: v(4),
    },
    rowTitleWrap: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
    },
    statusDot: {
      width: s(7),
      height: s(7),
      marginRight: s(8),
      borderRadius: s(999),
    },
    statusDotActive: {
      backgroundColor: theme.colors.successEmerald,
    },
    statusDotInactive: {
      backgroundColor: theme.colors.errorDark,
    },
    rowTitle: {
      flex: 1,
      marginRight: s(8),
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    rowTitleInactive: {
      color: theme.colors.textSecondary,
    },
    inactiveStatusLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(14),
      color: theme.colors.textSecondary,
    },
    rowRoleText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      marginLeft: s(12),
      minWidth: s(70),
      textAlign: 'right',
    },
    rowSubtext: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    rowSubtextInactive: {
      color: theme.colors.textTertiary,
    },
    phoneLinkText: {
      color: theme.colors.primaryDark,
    },
    rowNumberTextInactive: {
      color: theme.colors.textTertiary,
    },
    emptyCTA: {
      marginTop: v(6),
      paddingHorizontal: s(20),
      paddingVertical: v(12),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.primary,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'opacity 0.15s ease-out',
      }),
    } as any,
    emptyCTAHovered: {
      opacity: 0.92,
    } as any,
    emptyCTAPressed: {
      opacity: 0.88,
    },
    emptyCTAText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(18),
      color: theme.colors.textInverse,
    },
    footerLoading: {
      paddingVertical: v(12),
      alignItems: 'center',
    },
  });
